/**
 * Pure event-filtering + empty-state-diagnosis helpers (#3277). Kept pure (no
 * React, no DOM) so `useCalendarEventData` can compose them inside memos with
 * their original dependency arrays, and so `calendarEventFilters.char.test.ts`
 * can pin the load-bearing logic (#2724 Xavior gating, #2726 account scoping,
 * recently-unchecked drop, contact + freeform filtering, and the connected-empty
 * variant discriminator).
 */
import type { EventObject } from '../eventTypes';
import type { IntegrationSyncErrorCode } from '@/common/components/IntegrationSyncStatusBadge';

export interface FilterCalendarAccount {
  id: string;
  enabledForModule?: boolean;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncError?: string | null;
  syncErrorAt?: string | null;
}

export interface FilterCalendarData {
  loading?: boolean;
  error?: string | null;
  calendars?: Array<{ id: string; primary?: boolean; selected?: boolean }>;
}

/** `contact:<id>|...` filter-text prefix → the bare contact id (or null). */
export function parseContactFilterId(filterText?: string | null): string | null {
  const normalized = filterText?.trim();
  if (!normalized || !normalized.toLowerCase().startsWith('contact:')) return null;
  const token = normalized.slice('contact:'.length);
  const [id] = token.split('|');
  return id?.trim() || null;
}

/** Normalize a title for cross-account duplicate matching: lowercase, strip the
 *  possessive ("Vincent's" → "vincent" so it matches "Vincent"), drop any
 *  remaining apostrophes, collapse whitespace. */
function normalizeEventTitleKey(title: string | undefined): string {
  return (title ?? '')
    .toLowerCase()
    .replace(/[’']s\b/g, '')
    .replace(/[’']/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Collapse cross-account duplicate events. The same event present on two
 * connected accounts — a flight or meeting entered on both, a shared invite, or
 * a public holiday both subscribe to — syncs once per integration, so it renders
 * twice. Two rows are treated as the same event when their normalized title +
 * start time match (exact-title, so genuinely different entries are never
 * merged). Run AFTER recurrence expansion so independently-anchored recurring
 * copies collapse on the shared occurrence. Keeps the first row seen; preserves
 * input order. Recurring occurrences of one event stay distinct (different
 * startAt), so only true cross-row duplicates are removed.
 */
export function dedupeCrossAccountEvents(events: EventObject[]): EventObject[] {
  const seen = new Set<string>();
  const out: EventObject[] = [];
  for (const e of events) {
    const key = `${normalizeEventTitleKey(e.title)}|${e.startAt ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/**
 * Two normalized titles are "fuzzy-similar" for suspected-duplicate flagging
 * (#3952): exact normalized match, OR the smaller token set is a non-empty
 * SUBSET of the larger (token-containment). Catches word-omission cases like
 * "Magda Lunch" vs "Magda" while leaving genuinely different titles
 * ("Lunch with Sam" vs "Dinner with Pat") unflagged. Containment is chosen over
 * edit-distance because the observed failures are omitted-word cases, not typos,
 * and it has no magic ratio constant to tune. Empty keys never match.
 */
function titlesAreFuzzySimilar(aKey: string, bKey: string): boolean {
  if (!aKey || !bKey) return false;
  if (aKey === bKey) return true;
  const aTokens = new Set(aKey.split(' '));
  const bTokens = new Set(bKey.split(' '));
  const [smaller, larger] = aTokens.size <= bTokens.size ? [aTokens, bTokens] : [bTokens, aTokens];
  if (smaller.size === 0) return false;
  for (const token of smaller) {
    if (!larger.has(token)) return false;
  }
  return true;
}

/**
 * #3952: flag suspected-duplicate events that share an EXACT same-day time slot
 * (start + end + all-day mode) and have fuzzy-similar titles (see
 * `titlesAreFuzzySimilar`). Returns the set of every participating event id.
 *
 * TWO passes, both NON-DESTRUCTIVE (flag-only, never removes a row):
 *  1. SAME-ACCOUNT pass — within each `providerCalendarId` (or the `__xavior__`
 *     no-provider) bucket, pairwise-fuzzy at an exact slot. Two genuinely
 *     different SYNCED accounts (e.g. two Gmail calendars) are NEVER matched to
 *     each other here — that's `dedupeCrossAccountEvents`'s exact-title job.
 *  2. ORPHAN-vs-SYNCED pass — the literal QA case (#3952): a locally-created
 *     XAVIOR event that was never pushed (no `providerCalendarId`, so it can't be
 *     same-account bucketed against its synced twin) vs a same-slot, fuzzy-title
 *     SYNCED event ("Magda Lunch" created in Xavior + "Magda" imported by sync).
 *     The orphan WAS meant to land on that calendar, so the pair is the same
 *     real appointment. We deliberately match the Xavior bucket against ANY
 *     single synced event at the slot — two synced-vs-synced rows still never
 *     match across providers (neither is an orphan), preserving pass-1 scoping.
 *
 * Contrast with `dedupeCrossAccountEvents`: that is exact-title + DESTRUCTIVE
 * (removes the row) for legitimate cross-account copies; this is fuzzy +
 * flag-only for suspected dupes the user resolves by hand.
 *
 * Pure, order-independent, allocation-light: no React, no DOM, no mutation of
 * the input array.
 */
export function computeSuspectedDuplicateIds(events: EventObject[]): Set<string> {
  const slotKey = (e: EventObject) => `${e.startAt}|${e.endAt ?? ''}|${e.allDay ? 'D' : 'T'}`;

  // Pass 1 — same-slot, same-account buckets.
  const buckets = new Map<string, EventObject[]>();
  for (const e of events) {
    const accountBucket = e.providerCalendarId ?? '__xavior__';
    const key = `${accountBucket}|${slotKey(e)}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(e);
    else buckets.set(key, [e]);
  }

  const flagged = new Set<string>();
  for (const bucket of buckets.values()) {
    if (bucket.length < 2) continue;
    const keys = bucket.map((e) => normalizeEventTitleKey(e.title));
    for (let i = 0; i < bucket.length; i++) {
      for (let j = i + 1; j < bucket.length; j++) {
        if (titlesAreFuzzySimilar(keys[i], keys[j])) {
          flagged.add(bucket[i].id);
          flagged.add(bucket[j].id);
        }
      }
    }
  }

  // Pass 2 — local Xavior orphan vs same-slot synced twin (the QA case). Group
  // by SLOT only, then within a slot fuzzy-compare each no-provider event against
  // each provider-bearing event. Synced-vs-synced and orphan-vs-orphan pairs are
  // left to pass 1 (so different synced providers still never match here).
  const slotBuckets = new Map<string, EventObject[]>();
  for (const e of events) {
    const bucket = slotBuckets.get(slotKey(e));
    if (bucket) bucket.push(e);
    else slotBuckets.set(slotKey(e), [e]);
  }
  for (const bucket of slotBuckets.values()) {
    if (bucket.length < 2) continue;
    const orphans = bucket.filter((e) => !e.providerCalendarId);
    const synced = bucket.filter((e) => !!e.providerCalendarId);
    if (orphans.length === 0 || synced.length === 0) continue;
    for (const o of orphans) {
      const oKey = normalizeEventTitleKey(o.title);
      for (const s of synced) {
        if (titlesAreFuzzySimilar(oKey, normalizeEventTitleKey(s.title))) {
          flagged.add(o.id);
          flagged.add(s.id);
        }
      }
    }
  }
  return flagged;
}

/**
 * #3952 one-click resolve: given a flagged event, find its same-slot,
 * same-account, fuzzy-similar-title counterpart from the same expanded set, and
 * decide WHICH of the two to delete (keep-one / delete-the-other — there is no
 * backend merge endpoint, so "merge" reduces to deleting the redundant copy).
 *
 * Returns `{ keep, remove }` (the same two events, role-assigned) or `null` when
 * `target` has no qualifying counterpart (e.g. it's not actually flagged, or a
 * third same-slot event broke the clean pair — see the multi-match guard).
 *
 * Which copy to remove (documented heuristic):
 *  - Prefer deleting the locally-created XAVIOR copy (no `providerCalendarId` —
 *    the un-pushed orphan that the silent-push-failure produces) and KEEPING the
 *    synced provider copy. This is the exact QA shape ("Magda Lunch" local +
 *    "Magda" synced): the synced row is the source of truth on the calendar.
 *  - Since the grouper is same-ACCOUNT scoped, two flagged events almost always
 *    share a `providerCalendarId` bucket (both synced) or both sit in the Xavior
 *    bucket. In that tie the providerCalendarId test below can't pick a winner;
 *    we then keep the OLDER row (lexicographically smaller id is a stable, test-
 *    deterministic proxy — real creation order isn't always on the object) and
 *    remove the newer, so the action is never a no-op and never random.
 *
 * If `target` participates in MORE than one fuzzy-similar same-slot pair (3+
 * lookalikes in one slot), we return `null` rather than guess — that's the
 * "open both for the user" fallback the caller honors by just showing the modal.
 */
export function findSuspectedDuplicateCounterpart(
  events: EventObject[],
  target: EventObject,
): { keep: EventObject; remove: EventObject } | null {
  const targetKey = normalizeEventTitleKey(target.title);
  if (!targetKey) return null;
  const targetBucket = target.providerCalendarId ?? '__xavior__';
  const targetIsOrphan = !target.providerCalendarId;
  const targetSlot = `${target.startAt}|${target.endAt ?? ''}|${target.allDay ? 'D' : 'T'}`;

  const matches = events.filter((e) => {
    if (e.id === target.id) return false;
    const slot = `${e.startAt}|${e.endAt ?? ''}|${e.allDay ? 'D' : 'T'}`;
    if (slot !== targetSlot) return false;
    // Mirror computeSuspectedDuplicateIds' two passes: same-account, OR the
    // orphan-vs-synced cross-bucket case (exactly one side is a Xavior orphan).
    const bucket = e.providerCalendarId ?? '__xavior__';
    const eIsOrphan = !e.providerCalendarId;
    const sameAccount = bucket === targetBucket;
    const orphanVsSynced = targetIsOrphan !== eIsOrphan;
    if (!sameAccount && !orphanVsSynced) return false;
    return titlesAreFuzzySimilar(targetKey, normalizeEventTitleKey(e.title));
  });

  // Ambiguous (3+ in the slot) → let the user decide (caller opens the modal).
  if (matches.length !== 1) return null;
  const other = matches[0];

  // Prefer removing the local (un-synced) orphan, keeping the synced copy.
  const targetIsLocal = !target.providerCalendarId;
  const otherIsLocal = !other.providerCalendarId;
  if (targetIsLocal !== otherIsLocal) {
    return targetIsLocal
      ? { keep: other, remove: target }
      : { keep: target, remove: other };
  }
  // Both same source: keep the older (smaller id, stable) row, remove the newer.
  return target.id <= other.id
    ? { keep: target, remove: other }
    : { keep: other, remove: target };
}

/** #2726: calendarId → accountId map for scope filtering. */
export function buildCalendarIdToAccountId(
  accountCalendars: Record<string, FilterCalendarData>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const [accId, data] of Object.entries(accountCalendars)) {
    for (const cal of data.calendars || []) {
      map.set(cal.id, accId);
    }
  }
  return map;
}

/**
 * #2724: should Xavior events (no providerCalendarId) be hidden? Gated on the
 * first-enabled account's primary calendar toggle. See the inline comment in
 * EventListModule for the full rationale (avoiding the async-load race).
 */
export function computeHideXaviorEvents(
  calendarAccounts: FilterCalendarAccount[],
  accountCalendars: Record<string, FilterCalendarData>,
): boolean {
  const enabledAccounts = calendarAccounts.filter(a => a.enabledForModule);
  if (enabledAccounts.length === 0) return false; // no calendars connected → keep Xavior events

  const gatingAccount = enabledAccounts[0];
  const calData = accountCalendars[gatingAccount.id];
  if (!calData || !calData.calendars || calData.calendars.length === 0) {
    return false;
  }
  const primaryCal = calData.calendars.find(c => c.primary);
  if (!primaryCal) {
    const anySelected = calData.calendars.some(c => c.selected);
    return !anySelected;
  }
  return !primaryCal.selected;
}

export interface FilterCalendarEventsArgs {
  allEvents: any[];
  hideXaviorEvents: boolean;
  calendarScope: string;
  calendarAccounts: FilterCalendarAccount[];
  calendarIdToAccountId: Map<string, string>;
  /**
   * The same per-account calendar-list state that builds `calendarIdToAccountId`
   * (#5564). Used to gate scope-filter readiness on the SCOPED account's own load
   * state rather than the global map size — see the fail-open comment below.
   */
  accountCalendars: Record<string, FilterCalendarData>;
  recentlyUncheckedCalendarIds: Set<string>;
  contactFilterId: string | null;
  filterText?: string | null;
}

/**
 * The full `events` memo body: #2724 Xavior drop → #2726 account-scope drop →
 * recently-unchecked drop → contact-link filter → freeform text filter.
 */
export function filterCalendarEvents(args: FilterCalendarEventsArgs): any[] {
  const {
    allEvents,
    hideXaviorEvents,
    calendarScope,
    calendarAccounts,
    calendarIdToAccountId,
    accountCalendars,
    recentlyUncheckedCalendarIds,
    contactFilterId,
    filterText,
  } = args;

  const normalizedFilter = filterText?.trim().toLowerCase();
  const xaviorFiltered = hideXaviorEvents
    ? allEvents.filter((event: EventObject) => !!event.providerCalendarId)
    : allEvents;
  // #5564: fail-open while the SCOPED account's calendar list is still loading.
  // The scope filter maps each synced event's `providerCalendarId` back to its
  // owning account through `calendarIdToAccountId`, which is built INCREMENTALLY
  // from `accountCalendars` (each account's list resolves via its own 2-hop
  // accounts→calendars fetch waterfall, and the whole record resets to `{}` on
  // every mount). Gating on the GLOBAL map being non-empty is too coarse for a
  // multi-account user: account A's calendars can land first (map already
  // non-empty) while account B — the one actually SCOPED — has not, so every
  // B-owned event's lookup still returns `undefined` and the B-scoped view
  // collapses to zero chips (the exact #5564 symptom). Instead gate on whether
  // the SCOPED account's own list has finished loading: ready only once
  // `accountCalendars` has an entry for it that is no longer `loading` (even if
  // it came back empty). While NOT ready we fail open (skip scope-filtering,
  // keep the events); once the scoped account has loaded we fail closed and run
  // the real filter — so a genuinely-empty scoped account correctly shows its
  // own (few/zero) events instead of everyone's. Mirrors the async-load defense
  // in `computeHideXaviorEvents` a few functions above.
  const scopedAccountData = accountCalendars[calendarScope];
  const scopeReady = !!scopedAccountData && !scopedAccountData.loading;
  const scopeFiltered = calendarScope === 'all' || !scopeReady
    ? xaviorFiltered
    : xaviorFiltered.filter((event: EventObject) => {
        if (!event.providerCalendarId) {
          const enabledAccounts = calendarAccounts.filter(a => a.enabledForModule);
          return enabledAccounts[0]?.id === calendarScope;
        }
        return calendarIdToAccountId.get(event.providerCalendarId) === calendarScope;
      });
  const filtered = recentlyUncheckedCalendarIds.size > 0
    ? scopeFiltered.filter((event: EventObject) => {
        if (!event.providerCalendarId) return true;
        return !recentlyUncheckedCalendarIds.has(event.providerCalendarId);
      })
    : scopeFiltered;

  if (contactFilterId) {
    return filtered.filter((event: any) => {
      const links = Array.isArray(event.links)
        ? event.links.map((value: any) => String(value))
        : Array.isArray(event.attributes?.links)
          ? event.attributes.links.map((value: any) => String(value))
          : [];
      return links.includes(contactFilterId);
    });
  }

  let result = filtered;
  if (normalizedFilter) {
    result = result.filter((event: any) => {
      const attributeText = event.attributes ? JSON.stringify(event.attributes) : '';
      const attendees = Array.isArray(event.attendees) ? event.attendees.join(' ') : '';
      const haystack = [
        event.title,
        event.name,
        event.summary,
        event.description,
        event.location,
        attendees,
        attributeText,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedFilter);
    });
  }

  return result;
}

export interface ModuleSyncError {
  syncErrorCode: IntegrationSyncErrorCode;
  syncError: string | null;
  syncErrorAt: string | null;
}

/**
 * Module-wide sync error: only set when EVERY enabled account is in the SAME
 * error state (same code). Used by the connected-but-empty surfaces.
 */
export function computeModuleSyncError(
  calendarAccounts: FilterCalendarAccount[],
): ModuleSyncError | null {
  const enabled = calendarAccounts.filter(a => a.enabledForModule);
  if (enabled.length === 0) return null;
  const withError = enabled.filter(a => a.syncErrorCode);
  if (withError.length !== enabled.length) return null;
  const firstCode = withError[0].syncErrorCode;
  if (!withError.every(a => a.syncErrorCode === firstCode)) return null;
  return {
    syncErrorCode: firstCode!,
    syncError: withError[0].syncError ?? null,
    syncErrorAt: withError[0].syncErrorAt ?? null,
  };
}

export type ConnectedEmptyVariant =
  | 'rate-limited'
  | 'all-disabled'
  | 'no-calendars'
  | 'no-events';

/**
 * Discriminator for the connected-but-empty surface: rate-limited → all-disabled
 * → no-calendars → no-events, with the calendar-list-loaded guard before the
 * no-calendars assertion (avoids flicker while lists load).
 */
export function computeConnectedEmptyVariant(
  moduleSyncError: ModuleSyncError | null,
  calendarAccounts: FilterCalendarAccount[],
  accountCalendars: Record<string, FilterCalendarData>,
): ConnectedEmptyVariant {
  if (moduleSyncError) return 'rate-limited';
  const enabledAccounts = calendarAccounts.filter(a => a.enabledForModule);
  if (calendarAccounts.length > 0 && enabledAccounts.length === 0) return 'all-disabled';
  if (enabledAccounts.length > 0) {
    const haveLoadedAtLeastOne = enabledAccounts.some(a => {
      const data = accountCalendars[a.id];
      return data && !data.loading && (data.calendars?.length ?? 0) > 0;
    });
    if (haveLoadedAtLeastOne) {
      const anySelected = enabledAccounts.some(a => {
        const data = accountCalendars[a.id];
        return data?.calendars?.some(c => c.selected) ?? false;
      });
      if (!anySelected) return 'no-calendars';
    }
  }
  return 'no-events';
}
